<script lang="ts" setup>
import { ref, computed, watch, type PropType } from 'vue';

interface TotalProjectConsumptionItem {
  cement1: number
  cement2: number
  additive1: number
  additive2: number
  additive3: number
  additive4: number
  additive5: number
  additive6: number
  accelerator: number
}

const props = defineProps({
  totalUsedMaterialFromEvents: {
    type: Object as PropType<TotalProjectConsumptionItem>,
    required: true,
    default: () => ({})
  }
});

const projectImage = ref<string | null>(null);
const isDragging = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

const totalMaterialFromEvents = computed(() => {
  return props.totalUsedMaterialFromEvents
    ? props.totalUsedMaterialFromEvents
    : null;
});

const getFromEventsAdditiveValue = (index: number) => {
  if (!totalMaterialFromEvents.value) return 0;
  const key = `additive${index}` as keyof typeof totalMaterialFromEvents.value;
  return (totalMaterialFromEvents.value[key] as number) || 0;
};
const fmt = (val: number | undefined) => {
  if (val === undefined || val === null) return '0';
  return val.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const emit = defineEmits(['image-uploaded', 'project-data-updated']);

const handleDragOver = (e: DragEvent) => {
  e.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = (e: DragEvent) => {
  e.preventDefault();
  isDragging.value = false;
};

const handleDrop = (e: DragEvent) => {
  e.preventDefault();
  isDragging.value = false;
  
  const files = e.dataTransfer?.files;
  if (files && files.length > 0) {
    const file = files[0];
    if (file) processFile(file);
  }
};

const handleFileSelect = (e: Event) => {
  const input = e.target as HTMLInputElement;
  if (input.files && input.files.length > 0) {
    const file = input.files[0];
    if (file) processFile(file);
  }
};

const processFile = (file: File) => {
  if (!file.type.startsWith('image/')) {
    alert('Please upload an image file');
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    projectImage.value = e.target?.result as string;
    emit('image-uploaded', projectImage.value);
  };
  reader.readAsDataURL(file);
};

const removeImage = () => {
  projectImage.value = null;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
  emit('image-uploaded', null);
};

const wcTestData = ref({
  marshViscosity: ['', '', '', ''],
  selfWeightControl: ['', '', '', ''],
  waterExcretion: ['', '', '', ''],
  unbundling: ['', '', '', '']
});

const additionalInfo = ref({
  rigging: '',
  startDrilling: '',
  curingTime: '',
  boreMeter: '',
  usedPackers: '',
  waterLeakage: '',
  waterLoss: '',
  comments: ''
});

watch([wcTestData, additionalInfo], () => {
  emit('project-data-updated', { wcTestData: wcTestData.value, additionalInfo: additionalInfo.value });
}, { deep: true });

</script>

<template>
  <v-container fluid>
    <v-card
      class="mb-6 cursor-pointer position-relative"
      :color="isDragging ? 'blue-lighten-5' : 'grey-lighten-5'"
      variant="outlined"
      style="border-style: dashed; border-width: 2px;"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
      @click="fileInput?.click()"
    >
      <input
        type="file"
        ref="fileInput"
        style="display: none"
        accept="image/*"
        @change="handleFileSelect"
      >

      <div v-if="!projectImage" class="d-flex flex-column align-center justify-center pa-10" style="min-height: 200px;">
        <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-camera</v-icon>
        <div class="text-body-1 text-medium-emphasis">Drag & drop project image here or click to upload</div>
      </div>

      <div v-else class="position-relative d-flex justify-center pa-4">
        <v-img
          :src="projectImage"
          max-height="400"
          contain
          class="rounded"
        ></v-img>
        <v-btn
          icon="mdi-close"
          color="error"
          size="small"
          class="position-absolute top-0 right-0 mt-2 mr-2"
          @click.stop="removeImage"
          variant="flat"
        ></v-btn>
      </div>
    </v-card>

    <!-- Event Information -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
        Total Consumption (Events/Scale)
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text v-if="totalMaterialFromEvents">
        <v-row dense>
          <v-col cols="6" sm="3" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement 1</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialFromEvents.cement1) }} kg</div></v-col>
          <v-col cols="6" sm="3" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement 2</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialFromEvents.cement2) }} kg</div></v-col>
          <v-col cols="6" sm="3" class="mb-2"><div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Accelerator</div><div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalMaterialFromEvents.accelerator) }} kg</div></v-col>
          <v-col cols="6" sm="3" class="mb-2" v-for="i in 6" :key="i">
            <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Additive {{ i }}</div>
            <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(getFromEventsAdditiveValue(i)) }} L</div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text v-else class="text-center text-medium-emphasis font-italic pa-8">
        No event data available
      </v-card-text>
    </v-card>

    <!-- WC Test Data -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
        WC Test Data
      </v-card-title>
      <v-divider></v-divider>
      <v-table density="compact">
        <thead>
          <tr>
            <th class="text-left font-weight-bold text-uppercase text-caption text-medium-emphasis">WC test</th>
            <th class="text-center font-weight-bold text-uppercase text-caption text-medium-emphasis">Test 1</th>
            <th class="text-center font-weight-bold text-uppercase text-caption text-medium-emphasis">Test 2</th>
            <th class="text-center font-weight-bold text-uppercase text-caption text-medium-emphasis">Test 3</th>
            <th class="text-center font-weight-bold text-uppercase text-caption text-medium-emphasis">Test 4</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(label, key) in { marshViscosity: 'Marsh-viscosity', selfWeightControl: 'Self-weight control', waterExcretion: 'Water excretion', unbundling: 'Unbundling' }" :key="key">
            <td class="font-weight-medium text-body-2">{{ label }}</td>
            <td v-for="i in 4" :key="i" class="pa-1">
              <v-text-field
                v-model="wcTestData[key][i-1]"
                variant="outlined"
                density="compact"
                hide-details
                class="centered-input"
                placeholder="-"
              ></v-text-field>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <!-- Additional Project Information -->
    <v-card elevation="2">
      <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
        Additional Project Information
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6"><v-textarea v-model="additionalInfo.rigging" label="Up and Down Rigging of Injection" variant="outlined" density="compact" rows="3" auto-grow></v-textarea></v-col>
          <v-col cols="12" md="6"><v-textarea v-model="additionalInfo.startDrilling" label="Start Drilling" variant="outlined" density="compact" rows="3" auto-grow></v-textarea></v-col>
          <v-col cols="12" md="6"><v-textarea v-model="additionalInfo.curingTime" label="Curing Time" variant="outlined" density="compact" rows="3" auto-grow></v-textarea></v-col>
          <v-col cols="12" md="6"><v-textarea v-model="additionalInfo.boreMeter" label="Bore Meter" variant="outlined" density="compact" rows="3" auto-grow></v-textarea></v-col>
          <v-col cols="12" md="6"><v-textarea v-model="additionalInfo.usedPackers" label="Used Packers" variant="outlined" density="compact" rows="3" auto-grow></v-textarea></v-col>
          <v-col cols="12" md="6"><v-textarea v-model="additionalInfo.waterLeakage" label="Water Leakage" variant="outlined" density="compact" rows="3" auto-grow></v-textarea></v-col>
          <v-col cols="12" md="6"><v-textarea v-model="additionalInfo.waterLoss" label="Water Loss Measurements" variant="outlined" density="compact" rows="3" auto-grow></v-textarea></v-col>
          <v-col cols="12" md="6"><v-textarea v-model="additionalInfo.comments" label="Comments" variant="outlined" density="compact" rows="3" auto-grow></v-textarea></v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>